/**
 * SummaryTiles — Seven-tile KPI row for the Inbox Cockpit
 * Authority: WI-UI-CLEANUP-001 Stage 2, SA-2 file ownership
 *
 * Props:
 *   summary       : object  — counts/deltas from GET /api/work/summary
 *   summaryError  : string|null — set when the figures could not be loaded
 *   filterTile    : string|null
 *   onFilterTile  : (id: string|null) => void
 *   loading       : boolean
 *
 * The prop NAMES matter and are not free to drift: they are the same names
 * CockpitLayout, useInboxCockpitState, the Inbox page and DealListPane use.
 * This component previously declared `activeFilter` / `onFilterChange` while
 * CockpitLayout passed `filterTile` / `onFilterTile`, so every tile threw
 * "onFilterChange is not a function" on click and silently did nothing —
 * live in production, unnoticed, because this file's own unit test rendered
 * it with its own names and CockpitLayout's test mocked it away. Wiring is
 * tested in __tests__/inboxSummaryWiring.test.jsx, which mocks neither.
 *
 * Tile order (fixed): Strong | Weak | Pass | Directional | Oldest | Overnight | KB Writes
 * Visual: KPI_TILE shell pattern (verbatim from KpiTilesRow.jsx), 22px Inter 600 count,
 *         DS_TYPOGRAPHY.microLabel label, accent bar via KPI_TILE.accentBar.byVerdict[tone].
 */

import React from 'react';
import { KPI_TILE, DS_TYPOGRAPHY, DS_COLORS } from '@/config/ui-invariants';
import { COCKPIT_LABELS } from '@/config/task-display-config';
import { Skeleton } from '@/components/ui/skeleton';

// ── Tile definitions (left-to-right order is authoritative) ──────────────────

const TILES = [
  {
    id: 'strong',
    label: COCKPIT_LABELS.tiles.strong,
    tone: 'strong',
    getValue: (s) => s?.strong?.count ?? 0,
    getDelta: (s) => s?.strong?.delta,
  },
  {
    id: 'weak',
    label: COCKPIT_LABELS.tiles.weak,
    tone: 'weak',
    getValue: (s) => s?.weak?.count ?? 0,
    getDelta: () => undefined,
  },
  {
    id: 'pass',
    label: COCKPIT_LABELS.tiles.pass,
    tone: 'unclear',
    getValue: (s) => s?.pass?.count ?? 0,
    getDelta: (s) => s?.pass?.delta,
  },
  {
    id: 'directional',
    label: COCKPIT_LABELS.tiles.directional,
    tone: 'unclear',
    getValue: (s) => s?.directional?.count ?? 0,
    getDelta: () => undefined,
  },
  {
    id: 'oldest',
    label: COCKPIT_LABELS.tiles.oldest,
    tone: 'moderate',
    // Oldest tile: show age_hours when items_ref present, else count, else '—'
    getValue: (s) => {
      const ref = s?.oldest?.items_ref;
      if (ref && ref.age_hours != null) return `${ref.age_hours}h`;
      const count = s?.oldest?.count;
      if (count == null) return '—';
      return count;
    },
    getDelta: () => undefined,
  },
  {
    id: 'overnight',
    label: COCKPIT_LABELS.tiles.overnight,
    tone: 'moderate',
    getValue: (s) => s?.overnight?.count ?? 0,
    getDelta: (s) => s?.overnight?.delta,
  },
  {
    id: 'kb_writes',
    label: COCKPIT_LABELS.tiles.kbWrites,
    tone: 'strong',
    getValue: (s) => s?.kb_writes?.count ?? 0,
    getDelta: (s) => s?.kb_writes?.delta,
  },
];

// Shown in place of every count when the figures could not be loaded.
const UNKNOWN_VALUE = '—';

// ── Delta badge ───────────────────────────────────────────────────────────────

function DeltaBadge({ delta }) {
  if (delta === undefined || delta === null || delta === 0) return null;
  const positive = delta > 0;
  return (
    <span
      style={{
        fontFamily: DS_TYPOGRAPHY.microLabel.fontFamily,
        fontSize: '11px',
        fontWeight: 700,
        color: positive ? DS_COLORS.text.success : DS_COLORS.text.error,
        marginLeft: '6px',
      }}
    >
      {positive ? `+${delta}` : `${delta}`}
    </span>
  );
}

// ── Individual tile ───────────────────────────────────────────────────────────

function SummaryTile({ tile, summary, filterTile, onFilterTile, unavailable }) {
  const accentColor =
    KPI_TILE.accentBar.byVerdict[tile.tone] || KPI_TILE.accentBar.byVerdict.unclear;
  const isActive = filterTile === tile.id;
  // When the figures could not be loaded, the tile shows "—" (unknown), NEVER
  // a zero. A zero is a claim about the business; an unloaded figure is not.
  const displayValue = unavailable ? UNKNOWN_VALUE : tile.getValue(summary);
  const delta = unavailable ? undefined : tile.getDelta(summary);

  function handleClick() {
    onFilterTile(isActive ? null : tile.id);
  }

  return (
    <button
      data-tile-id={tile.id}
      aria-pressed={isActive}
      onClick={handleClick}
      style={{
        // KPI_TILE.shell spread (verbatim KpiTilesRow pattern)
        ...KPI_TILE.shell,
        // Layout
        flex: 1,
        minWidth: '120px',
        minHeight: '72px',
        position: 'relative',
        overflow: 'hidden',
        // Active state: navy bottom border
        borderBottom: isActive
          ? `3px solid ${DS_COLORS.text.brand}`
          : KPI_TILE.shell.border,
        // Button resets
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        // Focus ring via outline (keyboard-accessible)
        outline: 'none',
      }}
      onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${DS_COLORS.text.brand}`; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Top accent bar */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: KPI_TILE.accentBar.height,
          background: accentColor,
          borderRadius: KPI_TILE.accentBar.borderRadius,
        }}
      />

      <div style={{ paddingTop: '8px' }}>
        {/* Count row */}
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <span
            style={{
              ...DS_TYPOGRAPHY.kpiValue,
              color: DS_COLORS.text.primary,
            }}
          >
            {displayValue}
          </span>
          <DeltaBadge delta={delta} />
        </div>

        {/* Label */}
        <p
          style={{
            ...DS_TYPOGRAPHY.microLabel,
            color: DS_COLORS.text.secondary,
            margin: '4px 0 0',
          }}
        >
          {tile.label}
        </p>
      </div>
    </button>
  );
}

// ── Container ─────────────────────────────────────────────────────────────────

export default function SummaryTiles({
  summary,
  summaryError = null,
  filterTile,
  onFilterTile,
  loading,
}) {
  if (loading) {
    return (
      <div
        data-testid="cockpit-summary-tiles"
        style={{ display: 'flex', gap: '12px', width: '100%' }}
      >
        {TILES.map((t) => (
          <Skeleton
            key={t.id}
            style={{ flex: 1, minWidth: '120px', height: '72px' }}
          />
        ))}
      </div>
    );
  }

  const unavailable = Boolean(summaryError);

  return (
    <div
      data-testid="cockpit-summary-tiles"
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
    >
      {/* A failed load must never be readable as "everything is zero". */}
      {unavailable && (
        <p
          role="status"
          data-testid="cockpit-summary-unavailable"
          style={{
            ...DS_TYPOGRAPHY.bodySm,
            color: DS_COLORS.text.error,
            fontWeight: 700,
            margin: 0,
          }}
        >
          {COCKPIT_LABELS.tiles.unavailable}
        </p>
      )}

      <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
        {TILES.map((tile) => (
          <SummaryTile
            key={tile.id}
            tile={tile}
            summary={summary}
            filterTile={filterTile}
            onFilterTile={onFilterTile}
            unavailable={unavailable}
          />
        ))}
      </div>
    </div>
  );
}
